import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MOBILE_METRICS from '../../styles/mobile-metrics';

/**
 * 데스크톱 전용 페이지네이션 컴포넌트
 * 
 * 모바일 버튼 스타일을 그대로 사용하여 일관성 유지
 * 데스크톱에서만 표시되고 모바일에서는 인피니티 스크롤 유지
 */
export const DesktopPagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  className = '',
  itemsPerPage = 10,
  totalItems = 0,
  showInfo = true,
  maxVisiblePages = 5,
  // 한 단 작은 변형(32px·12px). 크기의 대부분은 CSS 의 .compact 가 정하는데,
  // 현재 페이지 칸만 인라인 style 이라 클래스로 덮이지 않아 여기서 함께 가른다.
  compact = false
}) => {
  const [pageInputValue, setPageInputValue] = useState(String(currentPage));

  // currentPage가 변경되면 inputValue도 동기화
  useEffect(() => {
    setPageInputValue(String(currentPage));
  }, [currentPage]);

  // 페이지 번호 배열 생성
  const getPageNumbers = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // 시작 페이지 조정
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  // 현재 표시 중인 아이템 범위
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const chevron = compact ? 14 : 16;

  return (
    <div className={`desktop-pagination ${compact ? 'compact ' : ''}${className}`}>
      {/* 이전 버튼 */}
      <button 
        className="page-button"
        style={{ borderRadius: '1.5rem' }}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label="이전 페이지"
      >
        <ChevronLeft size={chevron} className="font-bold" />
      </button>
      
      {/* 페이지 번호들 */}
      <div className="flex gap-1">
        {/* 첫 페이지 */}
        {getPageNumbers()[0] > 1 && (
          <>
            <button
              className="page-button"
        style={{ borderRadius: '1.5rem' }}
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
            {getPageNumbers()[0] > 2 && (
              <span className="page-dots">...</span>
            )}
          </>
        )}
        
        {/* 중간 페이지들 */}
        {getPageNumbers().map(pageNum => {
          if (currentPage === pageNum) {
            const inputWidth = Math.max(compact ? 2 : 2.5, String(pageInputValue).length * 0.6 + (compact ? 1.1 : 1.5));
            return (
              <input
                key={pageNum}
                type="text"
                value={pageInputValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d+$/.test(value)) {
                    setPageInputValue(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const newPage = parseInt(pageInputValue);
                    if (newPage >= 1 && newPage <= totalPages) {
                      e.target.blur();
                      setTimeout(() => {
                        onPageChange(newPage);
                      }, 100);
                    } else {
                      setPageInputValue(String(currentPage));
                    }
                  }
                }}
                onMouseUp={(e) => e.preventDefault()}
                onFocus={(e) => e.target.select()}
                onBlur={() => setPageInputValue(String(currentPage))}
                className="page-button active font-bold"
                style={{
                  borderRadius: '1.5rem',
                  width: `${inputWidth}rem`,
                  minWidth: compact ? '2rem' : '2.5rem',
                  height: compact ? '32px' : '40px',
                  textAlign: 'center',
                  outline: 'none',
                  border: 'none',
                  cursor: 'text',
                  padding: '0 8px'
                }}
                aria-label="페이지 번호 입력"
              />
            );
          }
          return (
            <button
              key={pageNum}
              className="page-button"
              style={{ borderRadius: '1.5rem' }}
              onClick={() => handlePageChange(pageNum)}
              aria-label={`${pageNum} 페이지`}
            >
              {pageNum}
            </button>
          );
        })}
        
        {/* 마지막 페이지 */}
        {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
          <>
            {getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && (
              <span className="page-dots">...</span>
            )}
            <button
              className="page-button"
        style={{ borderRadius: '1.5rem' }}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}
      </div>
      
      {/* 다음 버튼 */}
      <button 
        className="page-button"
        style={{ borderRadius: '1.5rem' }}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        aria-label="다음 페이지"
      >
        <ChevronRight size={chevron} className="font-bold" />
      </button>
      

    </div>
  );
};

/**
 * 간단한 페이지네이션 (페이지 번호 없이)
 */
export const SimplePagination = ({ 
  currentPage = 1, 
  totalPages = 1, 
  onPageChange,
  className = ''
}) => {
  return (
    <div className={`desktop-pagination simple ${className}`}>
      <button 
        className="page-button"
        style={{ borderRadius: '1.5rem' }}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
      >
        <ChevronLeft size={16} />
        <span className="ml-1">이전</span>
      </button>
      

      
      <button 
        className="page-button"
        style={{ borderRadius: '1.5rem' }}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
      >
        <span className="mr-1">다음</span>
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

export default DesktopPagination;